import { CrossBlackIcon } from "@/src/Asset";
import { useRouter } from "next/router";
import React, { useEffect, useState } from "react";
import SearchBar from "./SearchBar";
import { CityFilterButtons } from "./FilterButtons";

const redirectToNewUrl = async (
  router: any,
  colleges: any,
  location: any,
  filterOptions: any,
  setIsRedirecting: any,
  cityUrl: any
) => {
  if (!colleges || colleges.length === 0 || !location) return;

  setIsRedirecting(true);

  const newLocation = cityUrl;

  if (!newLocation) {
    setIsRedirecting(false);
    return;
  }

  let finalUpdateValue;

  if (filterOptions?.course?.course_url) {
    finalUpdateValue = `${filterOptions?.course?.course_url}-colleges-in-${newLocation}`;
  } else if (filterOptions?.stream?.stream_name) {
    finalUpdateValue = `${filterOptions?.stream?.stream_name
      .toLowerCase()
      .replace(/\s+/g, "-")}-colleges-in-${newLocation}`;
  } else {
    finalUpdateValue = `colleges-in-${newLocation}`;
  }

  if (filterOptions?.specialization?.specialization_url) {
    finalUpdateValue = `${filterOptions?.specialization?.specialization_url}-colleges-in-${newLocation}`;
  }

  const updatedColleges = [...colleges];
  updatedColleges[updatedColleges.length - 1] = finalUpdateValue;

  await router.push({
    pathname: router.pathname,
    query: { ...router.query, colleges: updatedColleges },
  });

  setIsRedirecting(false); // Reset loading state after redirection
};

export default function CitySection({
  setSelectedFilterCategory,
  filterOptions,
  filterData,
  collegesUrl,
  handleModalToggle,
}: any) {
  const router = useRouter();
  const [isRedirecting, setIsRedirecting] = useState(false);

  //  START : add CITIES values
  const [citiesData, setCitiesData] = useState<any[]>([]);

  const filterColleges = (
    data: any[],
    countryName: any,
    stateName: any,
    streamId: any,
    courseId: any,
    specializationId: any
  ) => {
    return data.filter((college) => {
      // Skip colleges without city data
      if (!college.city) return false;

      const matchesCountry =
        countryName === "abroad"
          ? college.country?.country_url !== "study-in-india"
          : countryName
          ? college.country?.country_url === `study-in-${countryName}`
          : true;

      const matchesState = stateName
        ? college.state?.state_url === stateName ||
          college.state?.state_name?.toLowerCase() === stateName?.toLowerCase()
        : true;

      const matchesStream = streamId
        ? college.streams?.some((stream: any) => stream.id === streamId)
        : true;

      const matchesCourse = courseId
        ? college.course?.some((course: any) => course.id === courseId)
        : true;

      const matchesSpecialization = specializationId
        ? college.specializations?.some(
            (spec: any) => spec.id === specializationId
          )
        : true;

      // Apply all filters together to show only cities with matching colleges
      return (
        matchesCountry &&
        matchesState &&
        matchesStream &&
        matchesCourse &&
        matchesSpecialization
      );
    });
  };

  const getUniqueCities = (filteredData: any) => {
    const cityMap = new Map();

    // Only include cities that have colleges matching all filters
    filteredData.forEach((college: any) => {
      if (college.city && !cityMap.has(college.city.id)) {
        // Store city with additional metadata
        cityMap.set(college.city.id, {
          ...college.city,
          state_name: college.state?.state_name,
          state_url: college.state?.state_url,
          college_count: 1,
        });
      } else if (college.city) {
        // Update college count for this city
        const existingCity = cityMap.get(college.city.id);
        existingCity.college_count += 1;
      }
    });

    return Array.from(cityMap.values());
  };

  useEffect(() => {
    const countryName = filterOptions.country?.country_name
      ?.toLowerCase()
      .trim();
    const stateName =
      filterOptions.state?.state_url ??
      filterOptions.state?.state_name?.toLowerCase().trim();
    const streamId = filterOptions?.stream?.id;
    const courseId = filterOptions?.course?.id;
    const specializationId = filterOptions?.specialization?.id;

    const filteredResults = filterColleges(
      filterData,
      countryName,
      stateName,
      streamId,
      courseId,
      specializationId
    );

    const uniqueCities = getUniqueCities(filteredResults);
    setCitiesData(uniqueCities);
  }, [
    filterOptions.country,
    filterOptions.state,
    filterOptions.stream,
    filterOptions.course,
    filterOptions.specialization,
    filterData,
  ]);
  //  END : add CITIES values

  const [searchQuery, setSearchQuery] = useState("");
  const [filteredCities, setFilteredCities] = useState(citiesData);
  const [noMatchData, setNoMatchData] = useState(false);

  useEffect(() => {
    if (searchQuery.length >= 3) {
      setFilteredCities(() => {
        const lowerCaseQuery = searchQuery.toLowerCase();

        // Find states that match the query
        const matchingStates = citiesData.filter((city: any) => {
          return city.city_name.toLowerCase().includes(lowerCaseQuery);
        });

        if (matchingStates.length === 0) {
          setNoMatchData(true);
        } else {
          setNoMatchData(false);
        }

        // Find the remaining states that do not match any condition
        const nonMatchingStates = citiesData.filter(
          (city: any) => !matchingStates.includes(city)
        );

        // Prioritize matching states first
        return [...matchingStates, ...nonMatchingStates];
      });
    } else {
      setFilteredCities(citiesData);
    }
  }, [searchQuery, citiesData]);

  // Prevent body scroll when popup is open
  // useEffect(() => {
  //   document.body.style.overflow = "hidden";
  //   return () => {
  //     document.body.style.overflow = "auto";
  //   };
  // }, []);

  const handleCitySelect = async (cityUrl: string) => {
    if (isRedirecting) return;
    setIsRedirecting(true);

    try {
      await redirectToNewUrl(
        router,
        collegesUrl,
        location,
        filterOptions,
        setIsRedirecting,
        cityUrl
      );
      const screenWidth = window.innerWidth;
      if (screenWidth >= 1024) {
        setSelectedFilterCategory(null);
      } else {
        handleModalToggle();
      }
    } catch (error) {
      console.error("Redirection failed:", error);
    } finally {
      setIsRedirecting(false);
    }
  };

  return (
    <div>
      {/* Mobile view: */}
      <div className="lg:hidden w-full h-fit">
        <div className="sticky top-0 bg-white">
          {/* Header */}
          <div className="text-lg font-bold text-[#334988] mb-2">City</div>

          {/* Search Bar */}
          <SearchBar
            placeholder="Find City"
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
          />
        </div>

        {searchQuery.length >= 3 && noMatchData && (
          <p className="text-red-500 text-sm text-center mt-1">
            No Match Found
          </p>
        )}

        {/* scrollable content */}
        <div
          style={{
            overflow: "auto",
            msOverflowStyle: "none", // IE and Edge
            scrollbarWidth: "none",
          }}
          className="w-full min-h-fit sm:max-h-full overflow-x-auto overflow-y-auto flex flex-wrap sm:grid sm:grid-cols-2 gap-3 mt-4 mb-20"
        >
          <CityFilterButtons
            filteredCities={filteredCities}
            filterOptions={filterOptions}
            searchQuery={searchQuery}
            isRedirecting={isRedirecting}
            handleCitySelect={handleCitySelect}
          />
        </div>
      </div>

      {/* Desktop view: */}
      <div className="hidden lg:block absolute mt-3 left-[12%] !z-10">
        <div className="bg-white rounded-lg shadow-lg border w-[28rem] h-[25rem] overflow-y-auto">
          <div className="sticky top-0 bg-white px-4 pt-4 pb-2">
            {/* Header */}
            <div className="flex justify-between items-center mb-2">
              <div className="text-lg font-medium">City</div>
              <button
                onClick={() => setSelectedFilterCategory(null)}
                className="text-gray-600 hover:text-gray-900 text-2xl"
              >
                <CrossBlackIcon className="scale-75" />
              </button>
            </div>

            {/* Search Bar */}
            <SearchBar
              placeholder="Find City"
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
            />
          </div>

          {searchQuery.length >= 3 && noMatchData && (
            <p className="text-red-500 text-sm text-center mt-1">
              No Match Found
            </p>
          )}

          {/* Scrollable Content */}
          <div className="flex flex-wrap gap-x-3 gap-y-4 justify-start px-4 pb-4">
            <CityFilterButtons
              filteredCities={filteredCities}
              filterOptions={filterOptions}
              searchQuery={searchQuery}
              isRedirecting={isRedirecting}
              handleCitySelect={handleCitySelect}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
